import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, inject, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';

import { LocaleService } from '@next/core-lib/i18n';
import { SiteActions, SiteSelectors } from '@next/core-lib/site';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { BusinessPartner } from './models/business-partner-backoffice.model';
import {
  getBusinessPartners,
  clearBusinessPartnerResult,
} from './store/actions/business-partner-backoffice.actions';
import {
  getBusinessPartnerTotalNumberOfItems,
  selectBusinessPartners,
} from './store/selectors/business-partner-backoffice.selector';
import { SiteState } from '../shared/models/siteState.model';

@Component({
  selector: 'app-business-partner-backoffice',
  templateUrl: './business-partner-backoffice.component.html',
  styleUrls: ['./business-partner-backoffice.component.scss'],
  standalone: false,
})
export class BusinessPartnerBackofficeComponent implements OnInit, OnDestroy {
  @ViewChild('paginator', { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  private readonly store = inject(Store<SiteState>);
  private readonly locale = inject(LocaleService);
  private readonly route = inject(ActivatedRoute);

  dataSource: MatTableDataSource<BusinessPartner> = new MatTableDataSource();
  pageTitle = toSignal(this.store.select(SiteSelectors.selectPageTitle));
  defaultPageSize = 10;
  pageSizeOptions = [10, 25, 50];
  currentPage = 1;
  currentPageSize = 10;
  totalNumberOfItems = toSignal(this.store.select(getBusinessPartnerTotalNumberOfItems));

  form = this.formBuilder.group({
    search: [],
  });

  displayedColumnBusinessPartnerTable: string[] = [
    'type',
    'name',
    'street',
    'postalCode',
    'ubn',
    'brs',
    'oneSap',
    'sapLegacyFeedId',
    'sapLegacyPlantId',
    'eurofins',
    'kat',
  ];

  subscription: Subscription;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private location: Location,
  ) {
    effect(() => {
      const titleKey = this.route.snapshot.data['pageTitle'];
      if (titleKey) {
        this.store.dispatch(
          SiteActions.updatePageTitle({ pageTitle: this.locale.translate(titleKey) }),
        );
      }
    });
  }

  ngOnInit(): void {
    const keyword = this.activatedRoute.snapshot.queryParamMap.get('keyword');

    if (keyword) {
      this.form.controls.search.setValue(keyword);
      this.searchBusinessPartners(true);
    }

    this.subscription = this.store.select(selectBusinessPartners).subscribe((businessPartners) => {
      if (businessPartners) {
        this.dataSource.data = businessPartners;
      }
    });

    this.subscription.add(
      this.sort.sortChange.subscribe(() => {
        const direction = this.sort.direction || 'desc';
        if (this.sort.direction === 'desc') {
          this.sort.direction = 'asc';
        }

        this.sort.direction = direction;
        this.searchBusinessPartners(true);
        this.currentPage = 1;
      }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  resetPaginator() {
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.currentPage = 1;
  }

  searchBusinessPartners(resetPaginator: boolean) {
    if (resetPaginator === true) {
      this.resetPaginator();
      this.sort.start = 'desc';
    }

    const keyword = this.form.controls.search.value;
    this.store.dispatch(
      getBusinessPartners({
        keyword,
        currentPage: this.currentPage,
        currentPageSize: this.currentPageSize,
        sort: this.sort.direction,
      }),
    );
    const url = this.router
      .createUrlTree([], {
        relativeTo: this.activatedRoute,
        queryParams: { keyword },
        queryParamsHandling: 'merge',
      })
      .toString();
    this.location.go(url);
  }

  clearSearchbox() {
    this.form.controls.search.setValue('');
    this.store.dispatch(clearBusinessPartnerResult());
  }

  onPage(event: PageEvent) {
    this.currentPage = event.pageIndex + 1;
    this.currentPageSize = event.pageSize;
    this.searchBusinessPartners(false);
  }
}
